import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  AspectRatio,
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Select,
  Text,
  Textarea,
  useToast,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Code,
  Flex,
} from '@chakra-ui/react';
import {
  ListingFormat,
  ListingType,
} from '@Encore-FastStartup-Phase-Out/encore-shared';

import { useStore } from '../../hooks/useStore';
import ListingTypePill from '../common/ListingTypePill';
import {
  formatDateInput,
  formatTimeInput,
  isValidDateStr,
  parseDate,
} from '../../utils/date';
import { isValidTimeHHMMorHHMMSS, isValidTimeStr } from '../../utils/time';
import { isFiniteNum, nonEmpty } from '../../utils/string';
import EbaySvg from '../../assets/svg/ebay.svg';
import ConfirmCrossListModal from '../modals/ConfirmCrossListModal';
import ErrorMessagesModal from '../modals/ErrorMessagesModal';

import Section from './Section';
import InfoDisplay from './InfoDisplay';

export enum CrossListingStatus {
  CREATED = 'created',
  VIDEO_CREATED = 'video_created',
  VIDEO_UPLOADED = 'video_uploaded',
  PUBLISHED = 'published',
  FAILED = 'failed',
}

interface ActiveListingDetailsProps {
  postSaveAction?: () => void;
}

const ActiveListingDetails = ({
  postSaveAction,
}: ActiveListingDetailsProps) => {
  const {
    listingStore: {
      displayedActiveListing,
      updateListing,
      getEbayCrossListStatus,
      crossListToEbay,
      retryEbayCrossList,
    },
    categoryStore: { categories, getCategories },
    userStore: { isEbayAccountConnected },
  } = useStore();
  const [listing, setListing] = useState(displayedActiveListing);
  const isAuction = displayedActiveListing?.listingType === ListingType.AUCTION;
  const [loading, setLoading] = useState(false);
  const [isSellerEbayAccountConnected, setIsSellerEbayAccountConnected] =
    useState(false);
  const [ebayCrossListStatus, setEbayCrossListStatus] = useState<
    CrossListingStatus | undefined
  >(undefined);
  const [ebayErrorMessages, setEbayErrorMessages] = useState<
    (string | object)[] | undefined
  >(undefined);
  const [isCrossListModalOpen, setIsCrossListModalOpen] = useState(false);
  const [isCrossListing, setIsCrossListing] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  const toast = useToast();

  const typeLabel = (t?: string) => (t ? t.replace(/_/g, ' ') : 'MAX BID');
  const typeColor = (t?: string) =>
    t?.toUpperCase().includes('PRE') ? 'purple' : 'blue';

  useEffect(() => {
    setListing(displayedActiveListing);
  }, [displayedActiveListing]);

  const isFlash = displayedActiveListing?.format === ListingFormat.FLASH;

  const selectedCategoryId = listing?.categoryId ?? listing?.category?.id ?? '';

  const selectedSubCategoryId =
    listing?.subCategoryId ?? listing?.subCategory?.id ?? '';

  const selectedCategory = useMemo(
    () => categories?.find((c: any) => c.id === selectedCategoryId),
    [categories, selectedCategoryId],
  );

  const subcategories = useMemo(
    () => (selectedCategory?.subcategories ?? []) as any[],
    [selectedCategory],
  );

  const initialDurationSec = useMemo(() => {
    const start = Number(displayedActiveListing?.startTime ?? 0);
    const end = Number(displayedActiveListing?.endTime ?? 0);
    return end > start ? Math.floor((end - start) / 1000) : 0;
  }, [displayedActiveListing?.startTime, displayedActiveListing?.endTime]);

  const [flashDurationStr, setFlashDurationStr] = useState(
    initialDurationSec > 0 ? String(initialDurationSec) : '',
  );
  const [startDateStr, setStartDateStr] = useState(() =>
    formatDateInput(listing?.startTime),
  );
  const [startTimeStr, setStartTimeStr] = useState(() =>
    formatTimeInput(listing?.startTime),
  );
  const [endDateStr, setEndDateStr] = useState(() =>
    formatDateInput(listing?.endTime),
  );
  const [endTimeStr, setEndTimeStr] = useState(() =>
    formatTimeInput(listing?.endTime),
  );

  const startPrevRef = React.useRef({ date: startDateStr, time: startTimeStr });
  const endPrevRef = React.useRef({ date: endDateStr, time: endTimeStr });

  useEffect(() => {
    setFlashDurationStr(
      initialDurationSec > 0 ? String(initialDurationSec) : '',
    );
  }, [initialDurationSec]);

  // modify commitDateTime to allow seconds
  const commitDateTime = (
    field: 'startTime' | 'endTime',
    dateStr: string,
    timeStr: string,
    prevRef: React.MutableRefObject<{ date: string; time: string }>,
  ) => {
    const isValidDate = /^\d{4}-\d{2}-\d{2}$/.test(dateStr);
    const isValidTime = /^\d{2}:\d{2}(:\d{2})?$/.test(timeStr);

    if (!isValidDate || !isValidTime) {
      if (field === 'startTime') {
        setStartDateStr(prevRef.current.date);
        setStartTimeStr(prevRef.current.time);
      } else {
        setEndDateStr(prevRef.current.date);
        setEndTimeStr(prevRef.current.time);
      }
      return;
    }

    const ms = new Date(`${dateStr}T${timeStr}`).getTime();
    if (Number.isNaN(ms)) {
      if (field === 'startTime') {
        setStartDateStr(prevRef.current.date);
        setStartTimeStr(prevRef.current.time);
      } else {
        setEndDateStr(prevRef.current.date);
        setEndTimeStr(prevRef.current.time);
      }
      return;
    }

    handleChange(field, String(ms));

    prevRef.current = { date: dateStr, time: timeStr };
  };

  const durationSec =
    flashDurationStr === '' ? 0 : parseInt(flashDurationStr, 10);
  const videoUrl = displayedActiveListing?.video.replace('http://', 'https://');

  const isFormValid = useMemo(() => {
    if (!nonEmpty(listing?.title)) return false;

    if (!nonEmpty(selectedCategoryId)) return false;
    if (!nonEmpty(selectedSubCategoryId)) return false;

    if (!nonEmpty(listing?.massUnit)) return false;
    if (!isFiniteNum(listing?.weight)) return false;
    if (!isFiniteNum(listing?.width)) return false;
    if (!isFiniteNum(listing?.height)) return false;
    if (!isFiniteNum(listing?.length)) return false;

    if (!isAuction) {
      if (!isFiniteNum(listing?.inStock)) return false;
    }

    if (isFlash) {
      if (
        !isValidDateStr(startDateStr) ||
        !isValidTimeHHMMorHHMMSS(startTimeStr)
      )
        return false;
      if (!Number.isFinite(durationSec) || durationSec <= 0) return false;
    } else if (isAuction) {
      if (!isValidDateStr(endDateStr) || !isValidTimeHHMMorHHMMSS(endTimeStr))
        return false;
    }

    return true;
  }, [
    isAuction,
    isFlash,
    listing?.title,
    listing?.massUnit,
    listing?.weight,
    listing?.width,
    listing?.height,
    listing?.length,
    listing?.inStock,
    selectedCategoryId,
    selectedSubCategoryId,
    startDateStr,
    startTimeStr,
    endDateStr,
    endTimeStr,
    durationSec,
  ]);

  const handleSubmit = async () => {
    if (!isFormValid) {
      toast({
        title: 'Missing or invalid fields',
        description: 'Please complete the required fields before saving.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);

      const updatedListing = {
        ...displayedActiveListing,
        ...listing,
      };
      // we need to remove the bid field from the listing object before updating since it's not a valid field,
      // it's only used for display
      delete updatedListing.bid;
      delete updatedListing.maxBids;
      delete updatedListing.category;
      delete updatedListing.subCategory;

      if (isFlash) {
        const startMs = Number(updatedListing.startTime ?? 0);
        const endMs = startMs + durationSec * 1000;
        updatedListing.startTime = String(startMs);
        updatedListing.endTime = String(endMs);
      }

      await updateListing(updatedListing);
      toast({
        title: 'Success',
        description: 'Listing updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      if (postSaveAction) postSaveAction();
    } catch (e) {
      console.error(e);
      toast({
        title: 'Error',
        description: 'Failed to update listing',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setListing((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCrossListConfirm = async (data?: {
    price: number;
    quantity: number;
  }) => {
    try {
      setIsCrossListing(true);

      if (ebayCrossListStatus === CrossListingStatus.FAILED) {
        await retryEbayCrossList(displayedActiveListing.id);
      } else {
        await crossListToEbay(
          displayedActiveListing.id,
          data?.price,
          data?.quantity,
        );
      }

      toast({
        title: 'Success',
        description: 'Listing cross-listed to eBay successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setIsCrossListModalOpen(false);
    } catch (e) {
      console.error(e);
      toast({
        title: 'Error',
        description: 'Failed to cross-list to eBay',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCrossListing(false);
    }
  };

  useEffect(() => {
    const d = formatDateInput(listing?.startTime);
    const t = formatTimeInput(listing?.startTime);
    setStartDateStr(d);
    setStartTimeStr(t);
    startPrevRef.current = { date: d, time: t };

    const de = formatDateInput(listing?.endTime);
    const te = formatTimeInput(listing?.endTime);
    setEndDateStr(de);
    setEndTimeStr(te);
    endPrevRef.current = { date: de, time: te };
  }, [listing?.startTime, listing?.endTime]);

  useEffect(() => {
    (async () => {
      await getCategories();
      if (displayedActiveListing) {
        const isConnected = await isEbayAccountConnected(
          displayedActiveListing.user.id,
        );
        setIsSellerEbayAccountConnected(isConnected);

        const { status, errorMessages } = await getEbayCrossListStatus(
          displayedActiveListing.id,
        );
        setEbayCrossListStatus(status);
        setEbayErrorMessages(errorMessages);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedCategory) return;
    if (
      selectedSubCategoryId &&
      !subcategories.some((s) => s.id === selectedSubCategoryId)
    ) {
      setListing((prev) => ({ ...prev, subCategoryId: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategoryId, categories]);

  return (
    <div>
      <Box display="flex" gap={5} mb={5}>
        <AspectRatio ratio={1} flex={2}>
          <iframe title="video" src={videoUrl} allowFullScreen />
        </AspectRatio>
        <Text flex={1}>{displayedActiveListing?.title}</Text>
      </Box>
      <Section
        header="Max Bids & Pre-bids"
        content={
          displayedActiveListing.maxBids?.length ? (
            <Box w="100%" overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>#</Th>
                    <Th isNumeric>Amount</Th>
                    <Th>Type</Th>
                    <Th>User</Th>
                    <Th>Created</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {displayedActiveListing.maxBids.map((mb, i) => (
                    <Tr key={mb.id}>
                      <Td>{i + 1}</Td>
                      <Td isNumeric>{mb.maxBid}</Td>
                      <Td>
                        <Badge colorScheme={typeColor(mb.type)}>
                          {typeLabel(mb.type)}
                        </Badge>
                      </Td>
                      <Td>
                        <Code fontSize="xs">{mb.user.userName}</Code>
                      </Td>
                      <Td>{parseDate(mb.createdAt.toString()).date}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          ) : (
            <Text>No max bids or pre-bids yet.</Text>
          )
        }
      />
      <Section
        header="Listing Detail"
        content={
          <VStack align="start" spacing={1}>
            <InfoDisplay
              title={'Listing id:'}
              value={displayedActiveListing?.id}
            />
            <InfoDisplay
              title={'Listing Description:'}
              value={displayedActiveListing?.description}
            />
            <InfoDisplay
              title={'Listing Type:'}
              value={
                <ListingTypePill
                  type={displayedActiveListing?.listingType}
                  format={displayedActiveListing.format}
                />
              }
            />
            {ebayCrossListStatus && (
              <InfoDisplay
                title={'Ebay Cross List Status:'}
                value={
                  <Flex alignItems="center" gap={2}>
                    <Text>{ebayCrossListStatus.replace(/_/g, ' ')}</Text>
                    {ebayErrorMessages && ebayErrorMessages.length > 0 && (
                      <Button
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => setIsErrorModalOpen(true)}
                      >
                        View Errors ({ebayErrorMessages.length})
                      </Button>
                    )}
                  </Flex>
                }
              />
            )}
          </VStack>
        }
      />
      <Section
        header="Seller Information"
        content={
          <VStack align="start" spacing={1}>
            <InfoDisplay
              title="Seller username:"
              value={displayedActiveListing?.user?.userName || ''}
            />
            <InfoDisplay
              title="Email:"
              value={displayedActiveListing?.user?.email || ''}
            />
            <InfoDisplay
              title={
                <HStack>
                  <img src={EbaySvg} alt={'ebay-svg'} style={{ width: 50 }} />
                  <Text>connected</Text>
                </HStack>
              }
              value={''}
            />
          </VStack>
        }
      />
      <Section
        header="Edit Listing"
        content={
          <VStack align="start" spacing={3}>
            {isSellerEbayAccountConnected && !ebayCrossListStatus && (
              <Button
                colorScheme={'blue'}
                onClick={() => setIsCrossListModalOpen(true)}
              >
                {displayedActiveListing.listingType === ListingType.DEFAULT
                  ? 'Cross list to eBay'
                  : 'Create buy now listing and cross list to eBay'}
              </Button>
            )}
            {isSellerEbayAccountConnected &&
              ebayCrossListStatus === CrossListingStatus.FAILED && (
                <Button
                  colorScheme={'blue'}
                  onClick={() => setIsCrossListModalOpen(true)}
                >
                  Retry cross list to eBay
                </Button>
              )}
            <FormControl id="title">
              <FormLabel>Title</FormLabel>
              <Input
                placeholder="Title"
                value={listing.title}
                onChange={(e) => handleChange('title', e.target.value)}
              />
            </FormControl>
            <FormControl id="description">
              <FormLabel>Description</FormLabel>
              <Textarea
                placeholder="Description"
                value={listing.description}
                onChange={(e) => handleChange('description', e.target.value)}
              />
            </FormControl>
            <FormControl id="collectionSku">
              <FormLabel>Collection SKU</FormLabel>
              <Input
                placeholder="Collection SKU"
                value={listing.collectionSku || ''}
                onChange={(e) => handleChange('collectionSku', e.target.value)}
              />
            </FormControl>
            <FormControl id="categoryId">
              <FormLabel>Category</FormLabel>
              <Select
                placeholder="Select category"
                value={selectedCategoryId}
                onChange={(e) => {
                  const newCategoryId = e.target.value;
                  setListing((prev) => ({
                    ...prev,
                    categoryId: newCategoryId,
                    // clear subcategory so user picks a valid one for the new category
                    subCategoryId: '',
                  }));
                }}
                isDisabled={!categories?.length}
              >
                {categories?.map((cat: any) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.title}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl id="subCategoryId" mt={3}>
              <FormLabel>Subcategory</FormLabel>
              <Select
                placeholder={
                  selectedCategory
                    ? 'Select subcategory'
                    : 'Pick a category first'
                }
                value={selectedSubCategoryId || ''}
                onChange={(e) =>
                  setListing((prev) => ({
                    ...prev,
                    subCategoryId: e.target.value,
                  }))
                }
                isDisabled={!selectedCategory}
              >
                {subcategories.map((sub: any) => (
                  <option key={sub.id} value={sub.id}>
                    {sub.title}
                  </option>
                ))}
              </Select>
            </FormControl>

            {!isAuction && (
              <FormControl id="inStock">
                <FormLabel>In Stock</FormLabel>
                <Input
                  placeholder="In Stock"
                  type="number"
                  value={listing.inStock}
                  onChange={(e) =>
                    handleChange('inStock', parseInt(e.target.value))
                  }
                />
              </FormControl>
            )}
            <Heading size={'md'} mt={2}>
              Packaging details
            </Heading>
            <FormControl id="massUnit">
              <FormLabel>Mass Unit</FormLabel>
              <Select
                placeholder="Select mass unit"
                value={listing.massUnit}
                onChange={(e) => handleChange('massUnit', e.target.value)}
              >
                <option value="lbs">lbs</option>
                <option value="oz">oz</option>
              </Select>
            </FormControl>
            <FormControl id="weight">
              <FormLabel>Weight</FormLabel>
              <Input
                placeholder="Weight"
                type="number"
                value={listing.weight}
                onChange={(e) =>
                  handleChange('weight', parseInt(e.target.value))
                }
              />
            </FormControl>
            <FormControl id="width">
              <FormLabel>Width</FormLabel>
              <Input
                placeholder="Width"
                type="number"
                value={listing.width}
                onChange={(e) =>
                  handleChange('width', parseInt(e.target.value))
                }
              />
            </FormControl>
            <FormControl id="height">
              <FormLabel>Height</FormLabel>
              <Input
                placeholder="Height"
                type="number"
                value={listing.height}
                onChange={(e) =>
                  handleChange('height', parseInt(e.target.value))
                }
              />
            </FormControl>
            <FormControl id="length">
              <FormLabel>Length</FormLabel>
              <Input
                placeholder="Length"
                type="number"
                value={listing.length}
                onChange={(e) =>
                  handleChange('length', parseInt(e.target.value))
                }
              />
            </FormControl>
            {isFlash ? (
              <>
                <Heading size="md" mt={2}>
                  Flash Auction Timing
                </Heading>
                <FormControl id="flashStartTime">
                  <FormLabel>Start Time</FormLabel>
                  <HStack spacing={3}>
                    <Input
                      type="date"
                      value={startDateStr}
                      onChange={(e) => setStartDateStr(e.target.value)}
                      onBlur={() =>
                        commitDateTime(
                          'startTime',
                          startDateStr,
                          startTimeStr,
                          startPrevRef,
                        )
                      }
                    />
                    <Input
                      type="time"
                      step="1"
                      value={startTimeStr}
                      onChange={(e) => setStartTimeStr(e.target.value)}
                      onBlur={() =>
                        commitDateTime(
                          'startTime',
                          startDateStr,
                          startTimeStr,
                          startPrevRef,
                        )
                      }
                    />
                  </HStack>
                </FormControl>

                <FormControl id="flashDurationSec">
                  <FormLabel>Duration (seconds)</FormLabel>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    value={flashDurationStr}
                    placeholder="e.g. 120"
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^\d]/g, '');
                      setFlashDurationStr(v);
                    }}
                    onBlur={() => {
                      if (flashDurationStr === '') return;
                      setFlashDurationStr(
                        String(parseInt(flashDurationStr, 10)),
                      );
                    }}
                  />
                </FormControl>
                <FormControl id="snipePrice">
                  <FormLabel>Snipe Price</FormLabel>
                  <Input
                    placeholder="Snipe Price"
                    type="number"
                    value={listing.snipePrice ?? 0}
                    onChange={(e) =>
                      handleChange('snipePrice', parseInt(e.target.value))
                    }
                  />
                </FormControl>
              </>
            ) : (
              isAuction && (
                <FormControl id="endTime">
                  <FormLabel>End Time</FormLabel>
                  <HStack spacing={3}>
                    <Input
                      type="date"
                      value={endDateStr}
                      onChange={(e) => setEndDateStr(e.target.value)}
                      onBlur={() =>
                        commitDateTime(
                          'endTime',
                          endDateStr,
                          endTimeStr,
                          endPrevRef,
                        )
                      }
                    />
                    <Input
                      type="time"
                      step="1"
                      value={endTimeStr}
                      onChange={(e) => setEndTimeStr(e.target.value)}
                      onBlur={() =>
                        commitDateTime(
                          'endTime',
                          endDateStr,
                          endTimeStr,
                          endPrevRef,
                        )
                      }
                    />
                  </HStack>
                </FormControl>
              )
            )}
            <Button
              onClick={handleSubmit}
              colorScheme="blue"
              isLoading={loading}
              isDisabled={!isFormValid || loading}
            >
              Save
            </Button>
          </VStack>
        }
      />

      <ConfirmCrossListModal
        isOpen={isCrossListModalOpen}
        onClose={() => setIsCrossListModalOpen(false)}
        onConfirm={handleCrossListConfirm}
        isLoading={isCrossListing}
        listingTitle={displayedActiveListing?.title || ''}
        isBuyNowConversion={
          displayedActiveListing?.listingType !== ListingType.DEFAULT
        }
        isRetry={ebayCrossListStatus === CrossListingStatus.FAILED}
      />

      <ErrorMessagesModal
        isOpen={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
        errorMessages={ebayErrorMessages || []}
        title="eBay Cross-Listing Errors"
      />
    </div>
  );
};

export default observer(ActiveListingDetails);
